import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { CoreActions } from '@next/core-lib';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { UserSelectors } from '@next/core-lib/user';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import {
  catchError,
  exhaustMap,
  finalize,
  mergeMap,
  skipWhile,
  startWith,
  switchMap,
  take,
} from 'rxjs/operators';

import {
  FarmSettings,
  FarmSettingsModuleState,
  UpsertFarmSettingsResponse,
} from '../../models/farm-settings.model';
import { FarmSettingsService } from '../../services/farm-settings.service';
import * as farmSettingsActions from '../actions/farm-settings.actions';

@Injectable()
export class FarmSettingsEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject<Store<FarmSettingsModuleState>>(Store);
  private readonly farmSettingsService = inject(FarmSettingsService);
  private readonly locale = inject(LocaleService);

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  loadFarmSettings$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(farmSettingsActions.loadFarmSettings),
      switchMap((_) =>
        this.store.pipe(
          select(UserSelectors.selectLocation),
          skipWhile((location) => location === null || location === undefined),
          take(1),
        ),
      ),
      mergeMap(() =>
        this.farmSettingsService.getFarmSettings().pipe(
          switchMap((farmSettings: FarmSettings) =>
            of(farmSettingsActions.loadFarmSettingsSuccess({ farmSettings })),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              farmSettingsActions.loadFarmSettingsFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  upsertFarmSettings$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(farmSettingsActions.upsertFarmSettings),
      exhaustMap(({ farmSettings }) =>
        this.farmSettingsService.upsertFarmSettings(farmSettings).pipe(
          switchMap((response: UpsertFarmSettingsResponse) =>
            of(
              farmSettingsActions.upsertFarmSettingsSuccess({
                farmSettings: {
                  ...farmSettings,
                  id: response.id,
                },
              }),
              NotificationActions.successSnackbar({
                message: this.locale.translate('sustainability.farm-settings-saved'),
              }),
            ),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              farmSettingsActions.upsertFarmSettingsFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );
}
